import React, { forwardRef } from "react";
import { AlertTriangle, Clock } from "lucide-react";
import { Card } from "../../../../shared/card/card.tsx";
import { Badge, Button, type BadgeVariant } from "../../../../ui/index.ts";
import { DatePicker } from "../../../../ui/date-picker/date-picker.tsx";
import { AddNewEntryCard } from "../add-new-entry-card/add-new-entry-card.tsx";
import { TimesheetDataTable } from "../timesheet-data-table/timesheet-data-table.tsx";
import { TimesheetEntry } from "../../types.ts";
import type { ProjectAllocationRow } from "../../utils/timesheet-allocation.ts";
import { isTimesheetHistoricalViewOnly, stripTime } from "../../utils/timesheet-allowed-dates.ts";
import styles from "./timesheet-entry-card.module.css";
import clsx from "clsx";

interface TimesheetEntryCardProps {
  entries: TimesheetEntry[];
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  handleAddEntry: (entry: TimesheetEntry) => void;
  handleDeleteEntry: (id: string) => void;
  handleEditEntry: (updated: TimesheetEntry) => void;
  isSubmitted?: boolean;
  isExpired?: boolean;
  showExtendButton?: boolean;
  extensionHrStatus?: string | null;
  onExtendClick?: () => void;
  isExtendLoading?: boolean;
  projectAllocations?: ProjectAllocationRow[];
  getOtherEntriesMinutesForProject?: (projectId: number) => number;
  historicalViewOnly?: boolean;
  timesheetLockMessage?: string;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

const formatTimesheetHeaderDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

function getExtensionStatusDisplay(hrStatus: string | null | undefined): {
  label: string;
  variant: BadgeVariant;
} | null {
  const normalized = (hrStatus ?? "").trim().toUpperCase();
  if (normalized === "PENDING") {
    return { label: "In-progress", variant: "warning" };
  }
  if (normalized === "REJECTED") {
    return { label: "Rejected", variant: "danger" };
  }
  if (normalized === "APPROVED") {
    return { label: "Approved", variant: "success" };
  }
  return null;
}

type TimesheetDateTriggerProps = React.ComponentPropsWithoutRef<"button"> & {
  selectedDate: Date;
};

const TimesheetDateTrigger = forwardRef<HTMLButtonElement, TimesheetDateTriggerProps>(
  function TimesheetDateTrigger({ selectedDate, className, ...rest }, ref) {
    return (
      <button
        ref={ref}
        className={clsx(styles.dateTrigger, className)}
        {...rest}
        type="button"
      >
        {formatTimesheetHeaderDate(selectedDate)}
      </button>
    );
  }
);

TimesheetDateTrigger.displayName = "TimesheetDateTrigger";

const TimesheetEntryCard = ({
  entries,
  selectedDate,
  setSelectedDate,
  handleAddEntry,
  handleDeleteEntry,
  handleEditEntry,
  isSubmitted = false,
  isExpired = false,
  showExtendButton = false,
  extensionHrStatus = null,
  onExtendClick,
  isExtendLoading = false,
  projectAllocations,
  getOtherEntriesMinutesForProject,
  historicalViewOnly: historicalViewOnlyProp,
  timesheetLockMessage = "Timesheet window is closed.",
}: TimesheetEntryCardProps) => {
  const historicalViewOnly =
    historicalViewOnlyProp ?? isTimesheetHistoricalViewOnly(selectedDate);
  const today = stripTime(new Date());
  const atToday =
    stripTime(selectedDate).getTime() === today.getTime();
  const extensionStatusDisplay = getExtensionStatusDisplay(extensionHrStatus);

  return (
    <Card className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          <h2 className={clsx("heading-sm", styles.title)}>Daily Time Entry</h2>
          <p className={clsx("sub-heading-sm", styles.subtitle)}>
            Time-off allocation and usage
          </p>
        </div>
        <div className={styles.dateNav}>
          <button
            type="button"
            className={styles.arrowBtn}
            aria-label="Previous day"
            onClick={() => setSelectedDate(addDays(selectedDate, -1))}
          >
            &#8249;
          </button>
          <DatePicker
            selected={selectedDate}
            onChange={setSelectedDate}
            maxDate={new Date()}
            customInput={<TimesheetDateTrigger selectedDate={selectedDate} />}
          />
          <button
            type="button"
            className={styles.arrowBtn}
            aria-label="Next day"
            onClick={() => setSelectedDate(addDays(selectedDate, 1))}
            disabled={atToday}
          >
            &#8250;
          </button>
        </div>
      </div>
      {historicalViewOnly && (
        <div className={styles.pastDateBanner} role="status">
          <div className={styles.pastDateBannerMain}>
            <AlertTriangle
              size={22}
              className={styles.pastDateBannerIcon}
              aria-hidden
            />
            <p className={styles.pastDateBannerText}>
              {timesheetLockMessage}
            </p>
          </div>
          {extensionStatusDisplay || (showExtendButton && onExtendClick) ? (
            <div className={styles.extensionRequestActions}>
              {extensionStatusDisplay ? (
                <div className={styles.extensionRequestStatus}>
                  <span className={styles.extensionRequestLabel}>
                    Extension Request
                  </span>
                  <Badge
                    variant={extensionStatusDisplay.variant}
                    className={styles.requestPendingBadge}
                  >
                    {extensionStatusDisplay.label}
                  </Badge>
                </div>
              ) : null}
              {showExtendButton && onExtendClick ? (
                <Button
                  variant="accent"
                  size="sm"
                  className={styles.requestEditBtn}
                  onClick={onExtendClick}
                  disabled={isExtendLoading}
                  startIcon={<Clock size={14} aria-hidden />}
                >
                  Extend Request
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {!historicalViewOnly && !isSubmitted && !isExpired && (
        <AddNewEntryCard
          key={`${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1}-${selectedDate.getDate()}`}
          selectedDate={selectedDate}
          onAddEntry={handleAddEntry}
          projectAllocations={projectAllocations}
          getOtherEntriesMinutesForProject={getOtherEntriesMinutesForProject}
        />
      )}
      <TimesheetDataTable
        entries={entries}
        selectedDate={selectedDate}
        onDelete={handleDeleteEntry}
        onEdit={handleEditEntry}
        isSubmitted={isSubmitted}
        viewOnly={historicalViewOnly}
      />
    </Card>
  );
};

export default TimesheetEntryCard;
